import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Flame, Play } from 'lucide-react';
import { Link } from 'react-router-dom';
import Logo from './Logo';
import GameArtwork from './GameArtwork';
import client, { resolveApiAssetUrl } from '../lib/api';
import useSwipe from '../hooks/useSwipe';
import { useSettings } from '../context/SettingsContext';
import { cacheSliders, readCachedSliders } from '../lib/sliderCache';
import { slidersAt } from '../lib/sliderPlacement';
import { HOOKS, hook } from '../lib/domHooks';

// Popüler slot slaytı yalnızca vitrini dolduracak kadar oyun varsa çıkar;
// iki kapakla açılan yarım bir slayt banner'ların yanında bozuk görünür.
const POPULAR_SLIDE_MIN_GAMES = 4;
const POPULAR_SLIDE_SIZE = 6;

const HeroSlider = ({ onPlayGame }) => {
  // Son görülen banner'lar önbellekten gelir; böylece yanıt beklenirken vitrin
  // boş kalmaz (bkz. lib/sliderCache.js). Taze liste geldiğinde üzerine yazılır.
  const [slides, setSlides] = useState(readCachedSliders);
  const [popularGames, setPopularGames] = useState([]);
  const [popularSource, setPopularSource] = useState('popular');
  const [loaded, setLoaded] = useState(false);
  const [idx, setIdx] = useState(0);
  const settings = useSettings();
  const bannerSlides = slidersAt(slides).filter(
    (slide) => slide.cta_link !== '/spor' || settings.sports_enabled !== false,
  );
  const showPopularSlide = popularGames.length >= POPULAR_SLIDE_MIN_GAMES;
  // Banner'lardan sonra gelen dördüncü slayt: popüler slot oyunları.
  const visibleSlides = showPopularSlide
    ? [...bannerSlides, { id: '__popular__', popular: true }]
    : bannerSlides;

  useEffect(() => {
    let active = true;
    client.get('/sliders')
      .then(({ data }) => {
        const items = data.items || [];
        cacheSliders(items);
        if (active) setSlides(items);
      })
      .catch(() => {
        // Ağ hatasında önbellekten çizilen banner'lar korunur; boşaltmak
        // vitrini sebepsiz karartırdı.
      })
      .finally(() => {
        if (active) setLoaded(true);
      });
    const loadPopularGames = async () => {
      // Panelde öne çıkarılan oyunlar varsa slayt tamamen yöneticinin seçimi ve
      // sırasıyla gelir; seçim yoksa uç gerçek oynanma verisiyle doldurur.
      try {
        const { data } = await client.get('/games/showcase', {
          params: { strip: 'featured', category: 'slot', limit: POPULAR_SLIDE_SIZE },
        });
        if (active) {
          setPopularGames(data.items || []);
          setPopularSource(data.source === 'manual' ? 'featured' : 'popular');
        }
      } catch {
        if (active) setPopularGames([]);
      }
    };
    loadPopularGames();
    return () => { active = false; };
  }, []);

  // Sayaç idx'e de bağlıdır: parmakla kaydıran oyuncu seçtiği banner'ı tam
  // süre görsün, otomatik geçiş kaydırmanın hemen ardına düşmesin.
  useEffect(() => {
    if (visibleSlides.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % visibleSlides.length), 5500);
    return () => clearInterval(t);
  }, [visibleSlides.length, idx]);

  useEffect(() => {
    if (idx >= visibleSlides.length) setIdx(0);
  }, [idx, visibleSlides.length]);

  // Mobilde bannerlar parmakla çevrilir; oklar yalnızca sm ve üstünde çizilir.
  const swipe = useSwipe({
    enabled: visibleSlides.length > 1,
    onNext: () => setIdx((i) => (i + 1) % visibleSlides.length),
    onPrev: () => setIdx((i) => (i - 1 + visibleSlides.length) % visibleSlides.length),
  });

  // Yer tutucu bilinçli olarak metinsizdir: "KAMPANYALAR YÜKLENİYOR" gibi bir
  // başlık saniyelerce gerçek bir banner sanılıyordu. İskelet aynı oranı
  // kapladığı için banner geldiğinde sayfa da kaymaz.
  if (!loaded && visibleSlides.length === 0) {
    return (
      <div
        className="hero-slider hero-fallback hero-loading-banner aspect-[4/3] sm:aspect-[16/7] md:aspect-[8/3]"
        role="presentation"
        aria-hidden="true"
      >
        <div className="hero-orbit hero-orbit-one" />
        <div className="hero-orbit hero-orbit-two" />
        <div className="relative z-10 flex h-full items-center px-6 sm:px-10">
          <div className="w-full max-w-sm">
            <div className="hero-skeleton-bar h-3 w-24 rounded-full" />
            <div className="hero-skeleton-bar mt-4 h-7 w-3/4 rounded-lg sm:h-9" />
            <div className="hero-skeleton-bar mt-3 h-5 w-1/2 rounded-lg sm:h-7" />
            <div className="hero-loading-line mt-6" />
          </div>
        </div>
      </div>
    );
  }

  if (visibleSlides.length === 0) {
    return null;
  }
  return (
    <div
      {...hook(HOOKS.SLIDER)}
      className="hero-slider aspect-[4/3] sm:aspect-[16/7] md:aspect-[8/3] relative"
      {...swipe}
    >
      {visibleSlides.map((s, i) => (s.popular ? (
        <div
          key={s.id}
          className="absolute inset-0 transition-opacity duration-700"
          style={{ opacity: i === idx ? 1 : 0, pointerEvents: i === idx ? 'auto' : 'none' }}
        >
          <div className="absolute inset-0" style={{ background: 'linear-gradient(120deg, #0d0718 0%, #17102a 45%, #0b1a14 100%)' }} />
          <div className="hero-popular-slide absolute inset-0 flex flex-col justify-center gap-3 px-5 sm:px-9 md:px-10 pt-4 pb-9">
            <div className="hero-popular-heading flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-[var(--pusu-accent-light)]">
                  <Flame size={16} />
                  <span className="text-[11px] sm:text-xs font-bold uppercase tracking-[0.18em]">
                    {popularSource === 'featured' ? 'Yönetici Seçimi' : 'En Çok Oynananlar'}
                  </span>
                </div>
                <div className="text-2xl sm:text-3xl md:text-5xl font-black gold-text leading-none mt-1">
                  POPÜLER SLOT
                </div>
                <div className="text-lg sm:text-xl md:text-3xl font-black text-white leading-tight tracking-tight">
                  OYUNLARI
                </div>
              </div>
              <Link
                to="/slotlar"
                className="gold-btn hidden sm:inline-flex shrink-0 px-4 sm:px-5 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm items-center gap-2"
              >
                <Logo size="sm" linked={false} />
                <span className="ml-1">Tümünü Gör</span>
              </Link>
            </div>
            <div className="hero-popular-grid grid grid-cols-3 sm:grid-cols-6 gap-2 sm:gap-3">
              {popularGames.slice(0, POPULAR_SLIDE_SIZE).map((game) => (
                <button
                  key={game.id}
                  type="button"
                  onClick={() => (onPlayGame ? onPlayGame(game) : null)}
                  className="hero-popular-tile group relative aspect-[3/4] overflow-hidden rounded-lg border border-white/10"
                  aria-label={`${game.name} oyununu aç`}
                >
                  <GameArtwork src={game.image} alt={game.name} />
                  <span className="absolute inset-0 grid place-items-center bg-black/45 opacity-0 transition-opacity group-hover:opacity-100">
                    <span className="gold-btn rounded-full w-9 h-9 flex items-center justify-center">
                      <Play size={15} fill="var(--pusu-on-accent)" />
                    </span>
                  </span>
                  <span
                    className="absolute inset-x-0 bottom-0 truncate px-1.5 py-1 text-[10px] font-semibold text-white"
                    style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0), rgba(0,0,0,0.9))' }}
                  >
                    {game.name}
                  </span>
                </button>
              ))}
            </div>
            <Link to="/slotlar" className="hero-popular-all gold-btn sm:hidden px-4 py-2 rounded-full text-xs inline-flex items-center justify-center gap-2">
              Tüm Slotları Gör
            </Link>
          </div>
        </div>
      ) : (
        <div key={s.id} className="absolute inset-0 transition-opacity duration-700" style={{ opacity: i === idx ? 1 : 0 }}>
          {s.image && <img src={resolveApiAssetUrl(s.image)} alt={s.subtitle} className="absolute inset-0 w-full h-full object-cover" />}
          <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.4) 55%, rgba(0,0,0,0.2) 100%)' }} />
          <div className="absolute inset-0 flex items-center px-5 sm:px-9 md:px-10">
            <div className="max-w-xl pr-3 sm:pr-12">
              <div className="text-4xl sm:text-5xl md:text-6xl font-black gold-text leading-none">{s.title}</div>
              <div className="text-xl sm:text-2xl md:text-4xl font-black text-white mt-2 leading-tight tracking-tight">{s.subtitle}</div>
              <div className="text-xl sm:text-2xl md:text-4xl font-black gold-text leading-tight tracking-tight">{s.highlight}</div>
              {s.description && <p className="text-gray-300 mt-2 md:mt-3 max-w-md text-xs sm:text-sm line-clamp-2">{s.description}</p>}
              <Link
                to={s.cta_link || '/promosyonlar'}
                className="gold-btn mt-3 md:mt-4 px-4 sm:px-5 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm inline-flex items-center gap-2"
              >
                <Logo size="sm" linked={false} />
                <span className="ml-1">{s.cta_text || 'Detaylar'}</span>
              </Link>
            </div>
          </div>
        </div>
      )))}

      {visibleSlides.length > 1 && (
        <>
          <button onClick={() => setIdx((i) => (i - 1 + visibleSlides.length) % visibleSlides.length)} className="hidden sm:flex absolute left-3 top-1/2 -translate-y-1/2 side-icon-btn" style={{ width: 40, height: 40 }} aria-label="Önceki"><ChevronLeft size={18} /></button>
          <button onClick={() => setIdx((i) => (i + 1) % visibleSlides.length)} className="hidden sm:flex absolute right-3 top-1/2 -translate-y-1/2 side-icon-btn" style={{ width: 40, height: 40 }} aria-label="Sonraki"><ChevronRight size={18} /></button>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
            {visibleSlides.map((_, i) => (
              <button key={i} className={`hero-dot ${i === idx ? 'active' : ''}`} onClick={() => setIdx(i)} aria-label={`Slide ${i + 1}`} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default HeroSlider;
